"use client";

import { useId, useState, useCallback, type ChangeEvent } from "react";
import { InputBase } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { TextInputType } from "@/commonTypes/question";
import { InputMode } from "@/utils/inputAttributes";

export interface FloatingLabelInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  inputType?: TextInputType | string;
  inputMode?: InputMode | null;
  autoComplete?: string | null;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string | null;
  valid?: boolean;
  className?: string;
}

/**
 * Shared floating label input component.
 * Used by both TextInput and TextGroup components.
 */
export default function FloatingLabelInput({
  label,
  value,
  onChange,
  onBlur,
  inputType = TextInputType.TEXT,
  inputMode,
  autoComplete,
  disabled = false,
  error = false,
  errorMessage,
  valid = false,
  className = "",
}: FloatingLabelInputProps) {
  const inputId = useId();
  const errorId = useId();
  const [isFocused, setIsFocused] = useState(false);

  const hasValue = value.length > 0;
  const isFloating = hasValue || isFocused;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  const containerClasses = [
    "text-input-container",
    isFocused ? "focused" : "",
    error ? "error" : "",
    valid && !error ? "valid" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <div className={containerClasses}>
        <label
          htmlFor={inputId}
          className={`text-input-label ${isFloating ? "floating" : ""}`}
        >
          {label}
        </label>
        <InputBase
          id={inputId}
          type={inputType}
          // inputMode must go in inputProps to reach the actual <input> element
          inputProps={{ inputMode: inputMode ?? undefined }}
          autoComplete={autoComplete ?? undefined}
          className="text-input-field"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-describedby={error && errorMessage ? errorId : undefined}
        />
      </div>
      {error && errorMessage && (
        <div className="text-input-supporting-text" id={errorId} role="alert">
          <ErrorIcon className="text-input-error-icon" />
          <span className="text-input-error-message">{errorMessage}</span>
        </div>
      )}
    </>
  );
}
